import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

// Context
import { TimekeepingContext } from "../../context/domain/timekeeping/TimekeepingContext";
import { CoreContext } from "../../context/global/CoreContext";

// Hooks
import { useSignatureCanvas, useTimesheetSubmission } from "../../hooks/timekeeping";

// API calls
import {
  saveWorkerTimesheet,
  getWorkerTimesheetWeekEnding,
} from "../../services/api/timekeeping";

// UI components
import { BasicCard, Button } from "../../components/ui";
import { ModalWrapper } from "../../components/ui";

// Page specific features
import { UI_COPY } from "../../config";
import { useUiText } from "../../hooks/useUiText";

import Title from "../../features/timekeeping/workerTimesheet/Title";
import WorkerShifts from "../../features/timekeeping/workerTimesheet/WorkerShifts";
import SafetyMeeting from "../../features/timekeeping/workerTimesheet/SafetyMeeting";
import Checklist from "../../features/timekeeping/workerTimesheet/Checklist";

/**
 * Page where the worker reviews their timesheet for the current week. This provides
};

/**
 * Page where the worker reviews their timesheet for the current week. This provides
 * the last chance for them to edit their shifts. They agree and sign off on the
 * hours worked here.
 *
 * Page saves the workers timesheet to the database upon submission (for manager review)
 * and redirects to the weekly schedule page.
 *
 * @returns The worker timesheet page.
 */
export default function WorkerTimesheet() {
  const { user, appName } = useContext(CoreContext);
  const { completedShifts, setDate, weekEnd, fetchWeeklyShifts } =
    useContext(TimekeepingContext);
  const { language, t } = useUiText();
  const [isSafetyMeetingReviewed, setIsSafetyMeetingReviewed] = useState(false);
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  // Signature canvas hook
  const {
    signatureRef,
    signatureContainerRef,
    isSigned,
    clearSignature,
    getSignatureData,
    validateSignature,
    signatureCanvasOptions,
    signatureCanvasCanvasProps,
    onSignatureEnd,
  } = useSignatureCanvas();

  // Timesheet submission hook
  const {
    isSubmitting,
    showSuccessModal,
    handleSubmit,
    closeSuccessModal,
  } = useTimesheetSubmission({
    submitFunction: saveWorkerTimesheet,
    onSuccess: () => {},
    onError: (error) => {
      alert(error);
    },
  });

  // Force and update of the weekly shifts data when the page loads, as it can be loaded
  // from another timekeeping page which still has the data in context, but not alwasy
  // updated.
  useEffect(() => {
    /**
     * Refetches weekly shifts so this page is not stale after other timekeeping screens.
     *
     * @returns {Promise<void>}
     */
    async function updateWeeklyShifts() {
      await fetchWeeklyShifts();
    }
    updateWeeklyShifts();
  }, [fetchWeeklyShifts]);

  // Set page title
  useEffect(() => {
    document.title = t(UI_COPY.timesheet.pageTitle);
    appName.current = "WorkerTimesheet";
    /**
     * Loads the worker timesheet week-ending date from the API.
     *
     * @returns {Promise<void>}
     */
    async function fetchDate() {
      const response = await getWorkerTimesheetWeekEnding();
      setDate(response["week_ending"]);
    }
    fetchDate();
  }, [appName, setDate, language, t]);

  // Submit timesheet function reads from the context which is updated by the shifts
  // component when user edits their shifts.
  /**
   * Validates the worker signature and submits completed shifts.
   *
   * @returns {void}
   */
  const handleSubmitTimesheet = () => {
    // Validate signature
    const validation = validateSignature();
    if (!validation.valid) {
      alert(
        t(
          validation.error === "Signature component not ready"
            ? UI_COPY.timesheet.signatureNotReady
            : UI_COPY.timesheet.signatureRequired
        )
      );
      return;
    }

    // Get signature data
    const signatureData = getSignatureData();

    const dataToSubmit = {
      weekEnd: weekEnd,
      shifts: completedShifts,
      signature: signatureData,
    };

    handleSubmit(dataToSubmit);
  };

  /**
   * Closes the success modal and returns to the worker schedule.
   *
   * @returns {void}
   */
  const handleSuccessModalClose = () => {
    closeSuccessModal();
    navigate("/timekeeping/worker-schedule");
  };

  useEffect(() => {
    if (isSigned && isSafetyMeetingReviewed && completedShifts.length > 0 && !isEditing && !isSubmitting) {
      setIsSubmittable(true);
    } else {
      setIsSubmittable(false);
    }
  }, [isSigned, isSafetyMeetingReviewed, completedShifts, isEditing, isSubmitting]);

  return (
    <div className="max-w-[1024px] mx-auto px-2 md:px-4 mb-96 font-inter caret-transparent bg-custom-rsbg flex flex-col items-center justify-center p-4">
      <BasicCard
        bg="bg-gray-100"
        text={t(UI_COPY.timesheet.checkDetails)}
        className="font-bold w-full mb-4 mt-2 p-2"
      />
      <Title user={user} className="mb-4" />
      <WorkerShifts isEditing={isEditing} setIsEditing={setIsEditing} />

      {/* Safety Meeting Review */}
      <SafetyMeeting
        weekEnd={weekEnd}
        isReviewed={isSafetyMeetingReviewed}
        setIsReviewed={setIsSafetyMeetingReviewed}
      />

      <BasicCard
        bg="bg-gray-100"
        text={t(UI_COPY.timesheet.certifyHours)}
        className="font-semibold text-sm w-full m-4 p-2"
      />

      {/* Signature Canvas */}
      <div className="w-full mb-2">
        <div
          className="border border-gray-300 overflow-hidden rounded-t-md"
          ref={signatureContainerRef}
        >
          <div className="border-b font-bold border-gray-300 bg-gray-300 p-2 text-center rounded-t-md">
            {t(UI_COPY.timesheet.signature)}
          </div>

          <div className="flex flex-col items-center">
            <SignatureCanvas
              ref={signatureRef}
              options={signatureCanvasOptions}
              onEnd={onSignatureEnd}
              canvasProps={signatureCanvasCanvasProps}
            />
          </div>
        </div>
        <div className="flex justify-center">
          <Button
            text={t(UI_COPY.timesheet.clear)}
            variant={isSigned ? "warning" : "disabled"}
            className="w-full rounded-t-none"
            onClick={clearSignature}
          />
        </div>
      </div>

      <Checklist
        className="w-full"
        shifts={completedShifts.length > 0}
        signed={isSigned}
        saved={!isEditing}
        safetyMeetingReviewed={isSafetyMeetingReviewed}
      />
      <Button
        text={
          isSubmitting
            ? ""
            : showSuccessModal
              ? t(UI_COPY.timesheet.submitted)
              : t(UI_COPY.timesheet.submit)
        }
        icon={isSubmitting ? "spinner" : undefined}
        variant={
          showSuccessModal ? "disabled" : isSubmittable ? "primary" : "disabled"
        }
        className="mb-4 w-full"
        onClick={handleSubmitTimesheet}
      />

      <ModalWrapper isOpen={showSuccessModal} onClose={handleSuccessModalClose}>
        <div className="text-center p-4">
          <div className="mb-4">
            <div className="text-green-600 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t(UI_COPY.timesheet.successTitle)}
            </h2>
            <p className="text-gray-600 mb-4">
              {t(UI_COPY.timesheet.successManagerNotified)}
            </p>
            <p className="text-gray-600 mb-6">
              {t(UI_COPY.timesheet.successReturnHint)}
            </p>
          </div>
          <Button
            text={t(UI_COPY.timesheet.ok)}
            variant="primary"
            className="w-full"
            onClick={handleSuccessModalClose}
          />
        </div>
      </ModalWrapper>
    </div>
  );
}
